import { Button, Heading, Text } from '@react-spectrum/s2';
import { style as spectrumStyle } from '@react-spectrum/s2/style' with { type: 'macro' };

import { useConfettiBurst } from '@/spaces/onboarding/screens/useConfettiBurst';

import '../../onboarding/onboarding.css';

const ACCENT = '#2D5F4F';

type SuccessInterstitialAction = {
	label: string;
	onPress: () => void;
};

type SuccessInterstitialProps = {
	title: string;
	summary: string;
	primaryAction: SuccessInterstitialAction;
	secondaryAction?: SuccessInterstitialAction;
};

export const SuccessInterstitial = ({
	title,
	summary,
	primaryAction,
	secondaryAction,
}: SuccessInterstitialProps) => {
	const confettiHostRef = useConfettiBurst();

	return (
		<div
			className="animate-onb-fade-in flex h-full w-full items-center
				justify-center"
		>
			<div
				className="flex flex-col items-center px-8 text-center"
				style={{ maxWidth: 560 }}
			>
				<div
					className="relative mb-5 flex items-center justify-center"
					style={{ width: 96, height: 96 }}
				>
					<div
						style={{
							width: 80,
							height: 80,
							background: 'rgba(45, 95, 79, 0.10)',
							borderRadius: '50%',
							position: 'relative',
							zIndex: 2,
						}}
					>
						<svg
							viewBox="0 0 80 80"
							width="80"
							height="80"
							style={{ display: 'block' }}
						>
							<circle
								className="onb-success-check-circle"
								cx="40"
								cy="40"
								r="36"
								fill="none"
								stroke={ACCENT}
								strokeWidth={3}
								strokeLinecap="round"
							/>
							<path
								className="onb-success-check-tick"
								d="M24 41 L36 53 L57 30"
								fill="none"
								stroke={ACCENT}
								strokeWidth={4.5}
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
					</div>
					<div
						ref={confettiHostRef}
						aria-hidden="true"
						style={{
							position: 'absolute',
							top: '50%',
							left: '50%',
							width: 0,
							height: 0,
							pointerEvents: 'none',
							zIndex: 1,
						}}
					/>
				</div>

				<Heading
					level={2}
					styles={spectrumStyle({
						font: 'heading',
						fontSize: 'heading',
						fontWeight: 'bold',
					})}
					UNSAFE_className="onb-success-text-in mb-3"
					UNSAFE_style={{ animationDelay: '800ms' }}
				>
					{title}
				</Heading>

				<Text
					styles={spectrumStyle({
						font: 'body',
						fontSize: 'body-sm',
						color: 'body',
					})}
					UNSAFE_className="onb-success-text-in mb-8"
					UNSAFE_style={{ animationDelay: '950ms' }}
				>
					{summary}
				</Text>

				<div
					className="onb-success-text-in flex items-center gap-3"
					style={{ animationDelay: '1150ms' }}
				>
					{secondaryAction && (
						<Button variant="secondary" onPress={secondaryAction.onPress}>
							{secondaryAction.label}
						</Button>
					)}
					<Button variant="accent" onPress={primaryAction.onPress}>
						{primaryAction.label}
					</Button>
				</div>
			</div>
		</div>
	);
};
